import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pages with dark hero backgrounds (use light text when not scrolled)
  const darkHeroPages = ['/', '/cascais', '/sintra', '/lisbon', '/obidos', '/tomar', '/evora', '/nazare', '/peniche'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);
  
  // Determine if we should use dark text (scrolled OR on light background page)
  const useDarkText = isScrolled || !hasDarkHero;
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navLinks = [
    { label: 'Property Details', href: '/', isRoute: true, scrollToTop: true },
    { label: 'Gallery', href: '/gallery', isRoute: true }
  ];
  
  const exploreLinks = [
    { label: 'Cascais (Home Base)', href: '/cascais', isRoute: true },
    { label: 'Sintra (15 min)', href: '/sintra', isRoute: true },
    { label: 'Lisbon (30 min)', href: '/lisbon', isRoute: true },
    { label: 'Óbidos (1 hour)', href: '/obidos', isRoute: true },
    { label: 'Nazaré (1 hour)', href: '/nazare', isRoute: true },
    { label: 'Peniche (2 hours)', href: '/peniche', isRoute: true },
    { label: 'Tomar (2 hours)', href: '/tomar', isRoute: true },
    { label: 'Évora (2 hours)', href: '/evora', isRoute: true }
  ];
  
  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    
    if (link.scrollToTop) {
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (link.isRoute) {
      navigate(link.href);
    } else {
      // If we're not on home page, go home first
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          element?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  const handleBookingClick = () => {
    setIsMobileMenuOpen(false);
    window.open('https://www.airbnb.com/rooms/1602726580833861863', '_blank', 'noopener,noreferrer');
  };
  
  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        useDarkText 
          ? 'bg-background/98 backdrop-blur-xl shadow-md' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button 
            onClick={() => navigate('/')}
            className={`text-2xl font-display font-semibold transition-colors duration-300 ${
              useDarkText ? 'text-foreground hover:text-primary' : 'text-background hover:text-background/80'
            }`}
          >
            Urban Oasis Cascais
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`text-sm font-medium transition-colors duration-300 ${
                  useDarkText 
                    ? 'text-foreground/80 hover:text-primary' 
                    : 'text-background/90 hover:text-background'
                }`}
              >
                {link.label}
              </button>
            ))}
            
            {/* Explore Dropdown */}
            <div 
              className="relative group"
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 ${
                  useDarkText 
                    ? 'text-foreground/80 hover:text-primary' 
                    : 'text-background/90 hover:text-background'
                }`}
              >
                Explore
                <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-48 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none bg-background border border-border shadow-lg overflow-hidden">
                  {exploreLinks.map((link) => (
                    <button
                      key={link.href}
                      onClick={() => handleNavClick(link)}
                      className="block w-full text-left px-4 py-3 text-sm text-foreground hover:bg-muted transition-colors duration-200"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <Button 
              size="default"
              className={`shadow-warm btn-transition ${
                useDarkText
                  ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  : 'bg-background hover:bg-background/90 text-foreground'
              }`}
              onClick={handleBookingClick}
            >
              Book Now
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors duration-300 ${
              useDarkText ? 'text-foreground hover:text-primary' : 'text-background hover:text-background/80'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-20 bg-background z-50 overflow-y-auto">
          <div className="px-6 py-8 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left py-4 text-lg font-medium text-foreground hover:text-primary border-b border-border/50 transition-all duration-300"
              >
                {link.label}
              </button>
            ))}
            
            {/* Mobile Explore Section */}
            <div className="pt-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Explore the Region</div>
              <div className="grid grid-cols-1 gap-1">
                {exploreLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNavClick(link)}
                    className="flex items-center justify-between w-full text-left py-3 px-4 text-base font-medium text-foreground/80 hover:text-primary hover:bg-muted/50 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none transition-all duration-300"
                  >
                    <span>{link.label.split(' (')[0]}</span>
                    <span className="text-sm text-muted-foreground">
                      {link.label.includes('(') ? `(${link.label.split('(')[1]}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="pt-6">
              <Button 
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-warm btn-transition py-6 text-lg"
                onClick={handleBookingClick}
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};